import React, { useState } from "react";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";

import { Badge } from "@/components/primitives/Badge";
import { Button } from "@/components/primitives/Button";
import { Input } from "@/components/primitives/Input";
import { Select } from "@/components/primitives/Select";
import { TextArea } from "@/components/primitives/TextArea";
import { HttpMethod, StepCreate, StepType } from "@/types";

import { DatabaseStepFields } from "./DatabaseStepFields";
import { MultipartStepFields } from "./MultipartStepFields";
import { SmsActionFields } from "./SmsActionFields";

const METHOD_OPTIONS: { value: HttpMethod; label: string }[] = [
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
  { value: "PUT", label: "PUT" },
  { value: "PATCH", label: "PATCH" },
  { value: "DELETE", label: "DELETE" },
  { value: "HEAD", label: "HEAD" },
  { value: "OPTIONS", label: "OPTIONS" },
];

const STEP_TYPE_OPTIONS: { value: StepType; label: string }[] = [
  { value: "http", label: "HTTP request" },
  { value: "http_multipart", label: "Multipart (file upload)" },
  { value: "database", label: "Database query" },
];

interface StepEditorProps {
  step: StepCreate;
  index: number;
  onChange: (index: number, step: StepCreate) => void;
  onRemove: (index: number) => void;
}

export const StepEditor: React.FC<StepEditorProps> = ({ step, index, onChange, onRemove }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const update = <K extends keyof StepCreate>(key: K, value: StepCreate[K]) => {
    onChange(index, { ...step, [key]: value });
  };

  const handleHeadersChange = (value: string) => {
    try {
      const parsed = value.trim() ? JSON.parse(value) : {};
      update("headers", parsed);
    } catch {
      // Allow invalid JSON while typing; validation happens on submit.
      update("headers", value as unknown as Record<string, string>);
    }
  };

  const handleRetryChange = (value: string) => {
    const intervals = value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean)
      .map((v) => parseInt(v, 10))
      .filter((n) => !isNaN(n) && n > 0);
    update("retry_intervals_minutes", intervals);
  };

  const headersString =
    typeof step.headers === "string"
      ? step.headers
      : JSON.stringify(step.headers || {}, null, 2);

  const baleActionIndex = step.action_configs.findIndex((a) => a.action_type === "bale_bot");
  const baleAction = baleActionIndex >= 0 ? step.action_configs[baleActionIndex] : null;
  const baleEnabled = baleAction ? baleAction.is_active : false;
  const baleTemplate = (baleAction?.config?.message_template as string | undefined) ?? "";

  const setBaleEnabled = (enabled: boolean) => {
    const configs = [...step.action_configs];
    if (baleActionIndex >= 0) {
      configs[baleActionIndex] = { ...configs[baleActionIndex], is_active: enabled };
    } else {
      configs.push({
        name: "Bale Bot Alert",
        action_type: "bale_bot",
        config: {},
        is_active: enabled,
      });
    }
    update("action_configs", configs);
  };

  const setBaleTemplate = (template: string) => {
    const configs = [...step.action_configs];
    if (baleActionIndex >= 0) {
      configs[baleActionIndex] = {
        ...configs[baleActionIndex],
        config: {
          ...configs[baleActionIndex].config,
          message_template: template || undefined,
        },
      };
    } else {
      configs.push({
        name: "Bale Bot Alert",
        action_type: "bale_bot",
        config: { message_template: template || undefined },
        is_active: true,
      });
    }
    update("action_configs", configs);
  };

  const isHttp = step.step_type === "http";
  const isMultipart = step.step_type === "http_multipart";

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-900/50">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h4 className="font-semibold text-slate-900 dark:text-white">
            Step {index + 1}: {step.name.trim() || "Untitled step"}
          </h4>
          <Badge variant="neutral">
            {STEP_TYPE_OPTIONS.find((o) => o.value === step.step_type)?.label}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded((v) => !v)}
            className="gap-1"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4" /> Collapse
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" /> Expand
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemove(index)}
            className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/30"
          >
            <Trash2 className="h-4 w-4" />
            Remove
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-6 border-t border-slate-200 px-5 py-5 dark:border-slate-800">
          <div>
            <h5 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Basic settings
            </h5>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Step name"
                value={step.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="e.g. Health check homepage"
                required
              />
              <Select
                label="Step type"
                value={step.step_type}
                options={STEP_TYPE_OPTIONS}
                onChange={(e) => update("step_type", e.target.value as StepType)}
              />
              <Input
                label="Period (minutes)"
                type="number"
                min={1}
                value={step.period_minutes}
                onChange={(e) => update("period_minutes", parseInt(e.target.value, 10) || 1)}
                required
              />
              <Input
                label="Retry intervals on failure (minutes, comma separated)"
                value={step.retry_intervals_minutes.join(", ")}
                onChange={(e) => handleRetryChange(e.target.value)}
                placeholder="15, 10, 5"
              />
            </div>
          </div>

          {isHttp && (
            <div>
              <h5 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                HTTP request
              </h5>
              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  label="HTTP method"
                  value={step.method ?? "GET"}
                  options={METHOD_OPTIONS}
                  onChange={(e) => update("method", e.target.value as HttpMethod)}
                  required
                />
                <Input
                  label="Request URL"
                  value={step.url ?? ""}
                  onChange={(e) => update("url", e.target.value)}
                  placeholder="https://api.example.com/health"
                  required
                />
                <Input
                  label="Content-Type"
                  value={step.content_type ?? ""}
                  onChange={(e) => update("content_type", e.target.value || null)}
                  placeholder="application/json"
                />
                <Input
                  label="Expected status code"
                  type="number"
                  min={100}
                  max={599}
                  value={step.expected_status ?? ""}
                  onChange={(e) => update("expected_status", parseInt(e.target.value, 10) || null)}
                  required
                />
                <Input
                  label="Timeout (seconds)"
                  type="number"
                  min={1}
                  max={3600}
                  value={step.timeout_seconds ?? 30}
                  onChange={(e) => update("timeout_seconds", parseInt(e.target.value, 10) || 30)}
                  required
                />
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Input
                  label="Response field to check (optional)"
                  value={step.response_assertion?.path ?? ""}
                  onChange={(e) => update("response_assertion", e.target.value ? {
                    path: e.target.value,
                    equals: step.response_assertion?.equals ?? "",
                  } : null)}
                  placeholder="instance.state"
                />
                <Input
                  label="Required response value"
                  value={step.response_assertion?.equals ?? ""}
                  disabled={!step.response_assertion}
                  onChange={(e) => update("response_assertion", {
                    path: step.response_assertion?.path ?? "",
                    equals: e.target.value,
                  })}
                  placeholder="open"
                />
              </div>
              <div className="mt-4 grid gap-4">
                <TextArea
                  label="HTTP headers (JSON)"
                  value={headersString}
                  onChange={(e) => handleHeadersChange(e.target.value)}
                  placeholder='{"Authorization": "Bearer token"}'
                  rows={3}
                />
                <TextArea
                  label="Request body"
                  value={step.body ?? ""}
                  onChange={(e) => update("body", e.target.value || null)}
                  placeholder="{}"
                  rows={4}
                />
              </div>
            </div>
          )}

          {isMultipart && (
            <div>
              <h5 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Multipart request (POST, form-data)
              </h5>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Request URL"
                  value={step.url ?? ""}
                  onChange={(e) => update("url", e.target.value)}
                  placeholder="http://172.21.1.51:8003/analyse_audio/"
                  required
                />
                <Input
                  label="Expected status code"
                  type="number"
                  min={100}
                  max={599}
                  value={step.expected_status ?? ""}
                  onChange={(e) => update("expected_status", parseInt(e.target.value, 10) || null)}
                  required
                />
                <Input
                  label="Timeout (seconds)"
                  type="number"
                  min={1}
                  max={3600}
                  value={step.timeout_seconds ?? 30}
                  onChange={(e) => update("timeout_seconds", parseInt(e.target.value, 10) || 30)}
                  required
                />
              </div>
              <div className="mt-4 grid gap-4">
                <TextArea
                  label="HTTP headers (JSON)"
                  value={headersString}
                  onChange={(e) => handleHeadersChange(e.target.value)}
                  placeholder='{"Authorization": "Bearer token"}'
                  rows={3}
                />
                <MultipartStepFields
                  config={step.multipart_config}
                  onChange={(multipartConfig) => update("multipart_config", multipartConfig)}
                />
              </div>
            </div>
          )}

          {!isHttp && !isMultipart && (
            <div>
              <h5 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Database query
              </h5>
              <DatabaseStepFields dbConfig={step.db_config} onChange={(dbConfig) => update("db_config", dbConfig)} />
            </div>
          )}

          <div>
            <h5 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Failure actions
            </h5>
            <label className="mb-4 flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={baleEnabled}
                onChange={(e) => setBaleEnabled(e.target.checked)}
                className="h-5 w-5 rounded border-slate-300 text-softnu-600 focus:ring-softnu-500 dark:border-slate-700 dark:bg-slate-800"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Send Bale bot alert on failure
              </span>
            </label>
            {baleEnabled && (
              <div className="mb-4">
                <TextArea
                  label="Message template (leave empty to use the default template)"
                  value={baleTemplate}
                  onChange={(e) => setBaleTemplate(e.target.value)}
                  placeholder="Available variables: {flow_name}, {step_name}, {url}, {method}, {expected_status}, {actual_status}, {error_message}, {response_body}, {started_at}"
                  rows={4}
                />
              </div>
            )}
            <SmsActionFields actions={step.action_configs} onChange={configs => update("action_configs", configs)} />
            <TextArea
              label="Action message on total failure"
              value={step.action_message ?? ""}
              onChange={(e) => update("action_message", e.target.value || null)}
              placeholder="Service homepage is unreachable. Escalate to on-call engineer."
              rows={2}
            />
          </div>
        </div>
      )}
    </div>
  );
};
